import Axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { apiUrl } from '../environment';
import store from "../components/store/Store";
import { userlogout } from '../components/slice/authService';
import { logout } from '../components/api/AuthService';
import { triggerBackgroundSamlLogout } from '../utils/authUtils';
import { clearSessionTokens, getAccessToken, getRefreshToken } from '../utils/authStorage';

/** Dispatched before storage clears so listeners (e.g. React Query) can reset. */
export const ACCESS_TOKEN_EXPIRED_EVENT = "fonemed-access-token-expired";

/** Dispatched after session access token is refreshed (e.g. SAML token rotation). */
export const AUTH_TOKEN_REFRESHED_EVENT = "fonemed-auth-token-refreshed";

export function notifyAuthTokenRefreshed() {
  window.dispatchEvent(new CustomEvent(AUTH_TOKEN_REFRESHED_EVENT));
}

const EXPIRY_SKEW_MS = 60_000;

let tokenExpiryLogoutGuard = false;

function isAccessTokenExpired(token) {
  if (!token || typeof token !== "string") {
    return false;
  }
  try {
    const { exp } = jwtDecode(token);
    if (typeof exp !== "number") {
      return true;
    }
    return exp * 1000 <= Date.now() + EXPIRY_SKEW_MS;
  } catch {
    return true;
  }
}

function broadcastLogoutToOtherTabs() {
  try {
    const channel = new BroadcastChannel("idle-sync");
    channel.postMessage({ type: "LOGOUT" });
    channel.close();
  } catch {
  }
}


function logoutDueToExpiredAccessToken() {
  if (tokenExpiryLogoutGuard) {
    return;
  }
  tokenExpiryLogoutGuard = true;
  try {
    window.dispatchEvent(new CustomEvent(ACCESS_TOKEN_EXPIRED_EVENT));
    broadcastLogoutToOtherTabs();
    const wasSaml = logout();
    store.dispatch(userlogout());
    if (wasSaml) {
      triggerBackgroundSamlLogout();
    }
  } finally {
    setTimeout(() => {
      tokenExpiryLogoutGuard = false;
    }, 100);
  }
}

export function handleAuthSessionHttpError(error) {
  const status = error.response?.status;
  if (status === 401 || status === 403) {
    const token = getAccessToken();
    if (token && isAccessTokenExpired(token)) {
      logoutDueToExpiredAccessToken();
      error.isTokenExpired = true;
    }
  } else if (
    !error.response &&
    error.code === "ERR_NETWORK" &&
    typeof error.message === "string"
  ) {
    const token = getAccessToken();
    if (token && isAccessTokenExpired(token)) {
      logoutDueToExpiredAccessToken();
      error.isTokenExpired = true;
    }
  }
  return Promise.reject(error);
}

function authRequestInterceptor(config) {
  const state = store.getState();
  const isLoggedIn = state.auth?.isLoggedIn;
  const token = getAccessToken();

  if (token) {
    config.headers.authorization = `${token}`;
  }

  if (isLoggedIn && !token) {
    const refreshToken = getRefreshToken() || state.auth?.refreshToken;
    if (!refreshToken) {
      clearSessionTokens();
      localStorage.clear();
      store.dispatch(userlogout());
    }
  }

  config.headers.Accept = 'application/json';
  return config;
}

export const axios = Axios.create({
  baseURL: apiUrl,
});

axios.interceptors.request.use(authRequestInterceptor);

axios.interceptors.response.use(
  (response) => response.data,
  (error) => handleAuthSessionHttpError(error)
);
